import { useEffect, useState } from 'react';
import { auditService, AuditLogRecord, FieldChange } from '@/services/auditService';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { formatDistanceToNow } from 'date-fns';
import { Edit, Plus, Trash2, Loader2, ChevronDown, ChevronRight, History } from 'lucide-react';

interface EntityActivityLogProps {
  entityType: string;
  entityId: string;
  limit?: number;
  maxHeight?: string;
}

export function EntityActivityLog({ 
  entityType, 
  entityId, 
  limit = 50,
  maxHeight = '400px',
}: EntityActivityLogProps) {
  const [logs, setLogs] = useState<AuditLogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const data = await auditService.getEntityHistory(entityType, entityId, limit);
        setLogs(data || []);
      } catch (error) {
        console.error('Failed to fetch activity logs:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLogs();
  }, [entityType, entityId, limit]);

  const toggleExpanded = (logId: string) => {
    setExpandedLogs(prev => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'create': return <Plus className="h-4 w-4 text-green-500" />;
      case 'update': return <Edit className="h-4 w-4 text-blue-500" />;
      case 'delete': 
      case 'bulk_delete': return <Trash2 className="h-4 w-4 text-red-500" />;
      default: return <History className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getActionBadge = (action: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      create: 'default',
      update: 'secondary',
      delete: 'destructive',
      bulk_delete: 'destructive',
    };
    return <Badge variant={variants[action] || 'outline'}>{action}</Badge>;
  };

  const getChangedFields = (log: AuditLogRecord): FieldChange[] => {
    if (!log.old_value || !log.new_value) return [];
    return auditService.getChangedFields(log.old_value, log.new_value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No activity recorded yet</p>
      </div>
    );
  }

  return (
    <ScrollArea style={{ maxHeight }} className="pr-4">
      <div className="space-y-3">
        {logs.map((log) => {
          const isExpanded = expandedLogs.has(log.id);
          const changedFields = log.action === 'update' ? getChangedFields(log) : [];
          const hasDetails = changedFields.length > 0;

          return (
            <Card key={log.id} className="p-3">
              <Collapsible open={isExpanded} onOpenChange={() => hasDetails && toggleExpanded(log.id)}>
                <div className="flex items-start gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={log.app_users?.avatar_url || undefined} />
                    <AvatarFallback className="text-xs">
                      {log.app_users?.full_name?.charAt(0) || 'S'}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getActionIcon(log.action)}
                      <span className="font-medium text-sm">
                        {log.app_users?.full_name || 'System'}
                      </span>
                      {getActionBadge(log.action)}
                      
                      {hasDetails && (
                        <CollapsibleTrigger asChild>
                          <button className="ml-auto p-1 hover:bg-muted rounded">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        </CollapsibleTrigger>
                      )}
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                    </p>
                    
                    {log.action === 'update' && changedFields.length > 0 && !isExpanded && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Changed: {changedFields.map(c => c.field).join(', ')}
                      </p>
                    )}
                  </div>
                </div>

                {hasDetails && (
                  <CollapsibleContent>
                    <div className="mt-3 pt-3 border-t space-y-2">
                      {changedFields.map((change, idx) => (
                        <div key={idx} className="text-sm">
                          <span className="font-medium text-muted-foreground">
                            {change.field}:
                          </span>
                          <div className="flex gap-2 mt-1">
                            <span className="text-red-500 line-through text-xs">
                              {formatValue(change.oldValue)}
                            </span>
                            <span className="text-muted-foreground">→</span>
                            <span className="text-green-500 text-xs">
                              {formatValue(change.newValue)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                )}
              </Collapsible>
            </Card>
          );
        })}
      </div>
    </ScrollArea>
  );
}

function formatValue(value: any): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
